/**
 * nav_region1 v2 (6) — 첫 방문 지역 제안의 «엣지 절반». 미들웨어가 Vercel 지오 헤더를 쿠키 kd_geo 로만 내려준다.
 *
 *   · 값 = ISO 3166-2:KR 시·도 접미 2자리(예: 부산 = '26'). Vercel `x-vercel-ip-country-region` 은 접두 없이 '26' 을 보내지만
 *     'KR-26' 꼴도 받아 둔다. 국가가 KR 이 아니거나 표에 없는 값이면 쿠키 없음.
 *   · 봇·크롤러 UA 에는 쿠키를 «내리지 않는다». 렌더는 이 쿠키를 읽지 않는다(서버 HTML·크롤러 응답 = 언제나 전국, 바이트 동일).
 *     ⛔ Vary: Cookie 금지 — 읽기는 클라이언트(수화 뒤) 전용이라 CDN 캐시를 가를 이유가 없다.
 *   · Redis·DB 호출 없음. 순수 문자열 모듈이라 Edge 안전(미들웨어가 import).
 *
 * 우리 시·도 코드(regions 표: 세종 36 · 제주 50 · 강원 51 · 전북 52 · 전남광주 12)와 ISO 번호는 «다르다»
 * (ISO 50 = 세종 · 49 = 제주). 그래서 쿠키에는 ISO 원값만 싣고, 이름 → 칩 노드 대응은 클라이언트(geo-suggest.ts)가 표 이름으로 한다.
 */
import { isoToKrRegion } from '@/lib/region-detection';

export const GEO_COOKIE = 'kd_geo';
/** 1일 — 위치는 자주 바뀌지 않지만 이사·출장 등은 다음 날 반영. 값이 바뀌면 그 요청에서 다시 쓴다. */
export const GEO_COOKIE_MAX_AGE = 60 * 60 * 24;

/**
 * 봇·자동화 UA. 미들웨어 CRAWLER_UA_RE(검색엔진 8종)보다 넓다 — 제안 쿠키는 «사람 브라우저» 에만.
 * 빈 UA 도 봇으로 본다.
 */
const BOT_UA_RE = /bot|crawl|spider|slurp|yeti|daumoa|daumcrawler|bingpreview|mediapartners|adsbot|lighthouse|headless|phantom|python|curl|wget|httpclient|okhttp|axios|node-fetch|undici|go-http|java\/|libwww|scrapy|facebookexternalhit|kakaotalk-scrap|embedly|preview|validator|monitor|uptime|gptbot|chatgpt|claude|perplexity|bytespider|petalbot|semrush|ahrefs|mj12|dotbot/i;

export function isBotUa(ua: string | null | undefined): boolean {
  const s = String(ua ?? '').trim();
  if (!s) return true;
  return BOT_UA_RE.test(s);
}

/** 강원(2023)·전북(2024) 특별자치도 전환 뒤 새 ISO 번호. region-detection 표(옛 번호 42·45)를 건드리지 않고 여기서 받는다. */
const ISO_NEW: Record<string, string> = { '51': '강원', '52': '전북' };

/** 헤더 원값 → ISO 접미 2자리(표에 있는 것만) · 아니면 null. */
export function normalizeIsoRegion(raw: string | null | undefined): string | null {
  const s = String(raw ?? '').trim().toUpperCase().replace(/^KR-/, '');
  if (!/^[0-9]{2}$/.test(s)) return null;
  return isoToKrRegion(s) || ISO_NEW[s] ? s : null;
}

/** ISO 접미 → 한글 시·도 짧은 이름(서울·부산…·전남·제주). 표에 없으면 null. */
export function isoSidoName(iso: string | null | undefined): string | null {
  const s = normalizeIsoRegion(iso);
  if (!s) return null;
  return isoToKrRegion(s) ?? ISO_NEW[s] ?? null;
}

/**
 * 이 요청에 kd_geo 를 새로 쓸 값. null = 쓰지 않음.
 *   GET 아님 · 봇 UA · 국가 KR 아님 · 지역 값 없음/무효 · 이미 같은 값 → null.
 */
export function middlewareGeoCookie(input: {
  method: string;
  ua: string | null | undefined;
  country: string | null | undefined;
  region: string | null | undefined;
  existing: string | null | undefined;
}): string | null {
  if (input.method !== 'GET') return null;
  if (isBotUa(input.ua)) return null;
  if (String(input.country ?? '').trim().toUpperCase() !== 'KR') return null;
  const iso = normalizeIsoRegion(input.region);
  if (!iso) return null;
  if (input.existing === iso) return null;
  return iso;
}

/** 클라이언트에서 kd_geo 읽기(수화 뒤에만). */
export function readGeoCookie(cookie: string | null | undefined): string | null {
  const m = /(?:^|;\s*)kd_geo=([^;]+)/.exec(String(cookie ?? ''));
  if (!m) return null;
  let v = m[1];
  try { v = decodeURIComponent(v); } catch { /* 원문 */ }
  return normalizeIsoRegion(v);
}
